import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Check, ChevronRight, ChevronLeft, Building2, Users, Briefcase,
  Mail, Lock, Eye, EyeOff, Shield, Smartphone, QrCode,
  Loader2, Sparkles, ArrowRight, Globe, Phone, Hash
} from "lucide-react";
import { setAuth } from "../../store/authSlice";
import api from "../../utils/api";
import { toast } from "sonner";
import Footer from "../../components/Footer";

const ACCOUNT_TYPES = [
  { value: "business", label: "Business Account", desc: "For companies looking to unify their financial, CRM, and operational data", icon: Building2, color: "#1B75BA" },
  { value: "partner", label: "Consulting Partner", desc: "For consultants and firms managing multiple client accounts", icon: Briefcase, color: "#D4A017" },
];

const ROLES = [
  { value: "owner", label: "Business Owner / CEO", desc: "Full access to financials, forecasting & team management" },
  { value: "consultant", label: "Fractional CFO / Consultant", desc: "Multi-client advisory and financial intelligence" },
  { value: "ops_manager", label: "Operations Manager", desc: "Operational KPIs, alerts & performance tracking" },
  { value: "accountant", label: "Accountant / Bookkeeper", desc: "Financial reports, accounting data & compliance" },
];

const INDUSTRIES = [
  "Technology / SaaS", "E-Commerce / Retail", "Financial Services", "Healthcare",
  "Manufacturing", "Professional Services", "Construction", "Real Estate",
  "Education", "Non-Profit", "Hospitality / Food", "Transportation / Logistics",
  "Media / Entertainment", "Agriculture", "Energy / Utilities", "Other",
];

const EMPLOYEE_COUNTS = [
  "Just me", "2-10", "11-50", "51-200", "201-500", "501-1000", "1000+",
];

const TOTAL_STEPS_BUSINESS = 6;
const TOTAL_STEPS_PARTNER = 5; // no role selection

function StepIndicator({ current, total, labels }) {
  return (
    <div className="flex items-center gap-1 mb-8">
      {Array.from({ length: total }, (_, i) => i + 1).map((s) => (
        <div key={s} className="flex items-center gap-1">
          <div className="flex items-center gap-1.5">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold transition-all"
              style={{
                backgroundColor: current > s ? "#D4A017" : current === s ? "#1B75BA" : "#F7F8FA",
                color: current >= s ? "white" : "#6B7491",
                border: current < s ? "1.5px solid #CDD2DE" : "none",
              }}>
              {current > s ? <Check className="w-3 h-3" /> : s}
            </div>
            {labels?.[s - 1] && (
              <span className="text-[9px] font-medium hidden md:inline" style={{ color: current >= s ? "#1B75BA" : "#9BA4B8" }}>
                {labels[s - 1]}
              </span>
            )}
          </div>
          {s < total && <div className="w-4 h-px mx-0.5" style={{ backgroundColor: current > s ? "#D4A017" : "#CDD2DE" }} />}
        </div>
      ))}
    </div>
  );
}

function OTPInput({ value, onChange, length = 6 }) {
  const refs = useRef([]);
  const digits = value.split("").concat(Array(length).fill("")).slice(0, length);

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const arr = digits.slice();
    arr[i] = val;
    onChange(arr.join(""));
    if (val && i < length - 1) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center">
      {digits.map((d, i) => (
        <input key={i} ref={el => refs.current[i] = el} data-testid={`otp-digit-${i}`}
          type="text" inputMode="numeric" maxLength={1} value={d}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          className="w-11 h-13 text-center text-lg font-bold rounded-xl outline-none transition-all"
          style={{ border: d ? "2px solid #1B75BA" : "1.5px solid #CDD2DE", backgroundColor: d ? "#EFF6FF" : "#F7F8FA", color: "#1C2040" }}
        />
      ))}
    </div>
  );
}

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    account_type: "business",
    role: "owner",
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    company_name: "",
    industry: "",
    employee_count: "",
    phone: "",
    website: "",
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [totpSecret, setTotpSecret] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [setup2fa, setSetup2fa] = useState(false);
  const [registeredToken, setRegisteredToken] = useState(null);
  const [registeredUser, setRegisteredUser] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const isPartner = form.account_type === "partner";
  const totalSteps = isPartner ? TOTAL_STEPS_PARTNER : TOTAL_STEPS_BUSINESS;
  const stepLabels = isPartner
    ? ["Type", "Details", "Company", "Verify", "Security"]
    : ["Type", "Role", "Details", "Company", "Verify", "Security"];

  // Map logical step to actual step for partner (skips role selection)
  const getLogicalStep = () => {
    if (isPartner && step >= 2) return step + 1; // shift steps up to skip role step
    return step;
  };
  const logicalStep = getLogicalStep();

  const canProceedStep1 = !!form.account_type;
  const canProceedStep2 = isPartner || !!form.role;
  const canProceedStep3 = form.name && form.email && form.password && form.password === form.confirmPassword && form.password.length >= 8;
  const canProceedStep4 = form.company_name && form.industry && form.employee_count;

  const handleSendCode = async () => {
    if (!form.email) return;
    setSendingCode(true);
    try {
      const res = await api.post("/auth/send-verification-code", { email: form.email });
      setDemoCode(res.data.demo_code || "");
      setCodeSent(true);
      toast.success("Verification code sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send code");
    } finally { setSendingCode(false); }
  };

  const handleVerifyCode = async () => {
    setVerifying(true);
    try {
      await api.post("/auth/verify-email-code", { email: form.email, code: verifyCode });
      setCodeVerified(true);
      toast.success("Email verified!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setVerifying(false); }
  };

  const handleRegister = async () => {
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        role: isPartner ? "consulting_partner" : form.role,
        account_type: form.account_type,
        company_name: form.company_name,
        industry: form.industry,
        employee_count: form.employee_count,
        phone: form.phone || null,
        website: form.website || null,
      };
      const res = await api.post("/auth/register", payload);
      setRegisteredToken(res.data.access_token);
      setRegisteredUser(res.data.user);
      localStorage.setItem("onpoint_token", res.data.access_token);
      localStorage.setItem("onpoint_refresh", res.data.refresh_token);
      // Move to security step
      setStep(prev => prev + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  const handleSetup2FA = async () => {
    try {
      const res = await api.post("/auth/2fa/setup");
      setTotpSecret(res.data.secret);
      setSetup2fa(true);
    } catch { toast.error("Failed to setup 2FA"); }
  };

  const handleVerify2FA = async () => {
    try {
      await api.post("/auth/2fa/verify", { code: totpCode });
      toast.success("2FA enabled!");
      finishRegistration();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid 2FA code");
    }
  };

  const finishRegistration = () => {
    if (registeredUser && registeredToken) {
      dispatch(setAuth({ user: registeredUser, token: registeredToken, refreshToken: localStorage.getItem("onpoint_refresh") }));
      toast.success("Welcome to OnPoint BI!");
      if (isPartner) {
        navigate("/partner/dashboard");
      } else {
        navigate("/dashboard");
      }
    }
  };

  const nextStep = () => {
    // On step before security (verify email step), register the user
    const verifyStep = isPartner ? 4 : 5;
    if (step === verifyStep && codeVerified) {
      handleRegister();
      return;
    }
    setStep(s => Math.min(s + 1, totalSteps));
  };
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  // Determine which logical content to show based on step
  const renderStep = () => {
    // Step 1: Account Type (same for both)
    if (step === 1) {
      return (
        <>
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1" style={{ color: "#1C2040" }}>Choose your account type</h2>
            <p className="text-sm" style={{ color: "#6B7491" }}>Select how you'll be using OnPoint BI</p>
          </div>
          <div className="space-y-3">
            {ACCOUNT_TYPES.map(t => (
              <button key={t.value} data-testid={`account-type-${t.value}`} type="button"
                onClick={() => update("account_type", t.value)}
                className="w-full text-left p-5 rounded-xl transition-all"
                style={{
                  border: form.account_type === t.value ? `2px solid ${t.color}` : "1.5px solid #CDD2DE",
                  backgroundColor: form.account_type === t.value ? `${t.color}08` : "white",
                }}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${t.color}15` }}>
                    <t.icon className="w-6 h-6" style={{ color: t.color }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold" style={{ color: form.account_type === t.value ? t.color : "#1C2040" }}>{t.label}</p>
                    <p className="text-xs mt-0.5" style={{ color: "#6B7491" }}>{t.desc}</p>
                  </div>
                  {form.account_type === t.value && (
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: t.color }}>
                      <Check className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
          <button data-testid="step-next" type="button" onClick={nextStep} disabled={!canProceedStep1}
            className="w-full h-11 mt-6 rounded-lg font-semibold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: "#1B75BA" }}>
            Continue <ChevronRight className="w-4 h-4" />
          </button>
        </>
      );
    }

    // Step 2: Role Selection (business only) - partners skip this
    if (step === 2 && !isPartner) {
      return (
        <>
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1" style={{ color: "#1C2040" }}>What's your role?</h2>
            <p className="text-sm" style={{ color: "#6B7491" }}>This helps us personalize your OnPoint experience</p>
          </div>
          <div className="space-y-3">
            {ROLES.map(r => (
              <button key={r.value} data-testid={`role-option-${r.value}`} type="button"
                onClick={() => update("role", r.value)}
                className="w-full text-left p-4 rounded-xl transition-all"
                style={{
                  border: form.role === r.value ? "2px solid #1B75BA" : "1.5px solid #CDD2DE",
                  backgroundColor: form.role === r.value ? "#F0F8FF" : "white",
                }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: form.role === r.value ? "#1B75BA" : "#1C2040" }}>{r.label}</p>
                    <p className="text-xs mt-0.5" style={{ color: "#6B7491" }}>{r.desc}</p>
                  </div>
                  {form.role === r.value && (
                    <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#1B75BA" }}>
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
          <div className="flex gap-3 mt-6">
            <button type="button" onClick={prevStep} className="h-11 px-5 rounded-lg font-medium text-sm flex items-center gap-1.5" style={{ border: "1.5px solid #CDD2DE", color: "#6B7491" }}>
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <button data-testid="step-next" type="button" onClick={nextStep} disabled={!canProceedStep2}
              className="flex-1 h-11 rounded-lg font-semibold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: "#1B75BA" }}>
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </>
      );
    }

    // Details step (step 2 for partner, step 3 for business)
    const detailsStep = isPartner ? 2 : 3;
    if (step === detailsStep) {
      return (
        <>
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1" style={{ color: "#1C2040" }}>Your details</h2>
            <p className="text-sm" style={{ color: "#6B7491" }}>Create your personal login credentials</p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "#1C2040" }}>Full Name</label>
              <input data-testid="register-name" type="text" value={form.name} onChange={e => update("name", e.target.value)}
                placeholder="John Smith" required
                className="w-full h-11 px-4 rounded-lg text-sm outline-none transition-all"
                style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #CDD2DE", color: "#1C2040" }}
                onFocus={e => e.target.style.borderColor = "#1B75BA"} onBlur={e => e.target.style.borderColor = "#CDD2DE"} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "#1C2040" }}>Work Email</label>
              <input data-testid="register-email" type="email" value={form.email} onChange={e => update("email", e.target.value)}
                placeholder="john@company.com" required
                className="w-full h-11 px-4 rounded-lg text-sm outline-none transition-all"
                style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #CDD2DE", color: "#1C2040" }}
                onFocus={e => e.target.style.borderColor = "#1B75BA"} onBlur={e => e.target.style.borderColor = "#CDD2DE"} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "#1C2040" }}>Password</label>
              <div className="relative">
                <input data-testid="register-password" type={showPass ? "text" : "password"} value={form.password}
                  onChange={e => update("password", e.target.value)} placeholder="Min. 8 characters" required
                  className="w-full h-11 px-4 pr-10 rounded-lg text-sm outline-none transition-all"
                  style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #CDD2DE", color: "#1C2040" }}
                  onFocus={e => e.target.style.borderColor = "#1B75BA"} onBlur={e => e.target.style.borderColor = "#CDD2DE"} />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2">
                  {showPass ? <EyeOff className="w-4 h-4" style={{ color: "#9BA4B8" }} /> : <Eye className="w-4 h-4" style={{ color: "#9BA4B8" }} />}
                </button>
              </div>
              {form.password && (
                <div className="flex gap-1 mt-2">
                  {[form.password.length >= 8, /[A-Z]/.test(form.password), /[a-z]/.test(form.password), /\d/.test(form.password)].map((ok, i) => (
                    <div key={i} className="h-1 flex-1 rounded-full" style={{ backgroundColor: ok ? "#D4A017" : "#E5E7EB" }} />
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "#1C2040" }}>Confirm Password</label>
              <input data-testid="register-confirm-password" type="password" value={form.confirmPassword}
                onChange={e => update("confirmPassword", e.target.value)} placeholder="Repeat password" required
                className="w-full h-11 px-4 rounded-lg text-sm outline-none transition-all"
                style={{ backgroundColor: "#F7F8FA", border: form.confirmPassword && form.password !== form.confirmPassword ? "1.5px solid #EA580C" : "1.5px solid #CDD2DE", color: "#1C2040" }}
                onFocus={e => e.target.style.borderColor = "#1B75BA"} onBlur={e => e.target.style.borderColor = form.confirmPassword && form.password !== form.confirmPassword ? "#EA580C" : "#CDD2DE"} />
              {form.confirmPassword && form.password !== form.confirmPassword && (
                <p className="text-[10px] mt-1 font-medium" style={{ color: "#EA580C" }}>Passwords do not match</p>
              )}
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button type="button" onClick={prevStep} className="h-11 px-5 rounded-lg font-medium text-sm flex items-center gap-1.5" style={{ border: "1.5px solid #CDD2DE", color: "#6B7491" }}>
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <button data-testid="step-next" type="button" onClick={nextStep} disabled={!canProceedStep3}
              className="flex-1 h-11 rounded-lg font-semibold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: "#1B75BA" }}>
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </>
      );
    }

    // Company Info step (step 3 for partner, step 4 for business)
    const companyStep = isPartner ? 3 : 4;
    if (step === companyStep) {
      return (
        <>
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1" style={{ color: "#1C2040" }}>
              {isPartner ? "About your practice" : "Company information"}
            </h2>
            <p className="text-sm" style={{ color: "#6B7491" }}>Help us tailor your experience</p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "#1C2040" }}>
                {isPartner ? "Firm / Practice Name" : "Company Name"}
              </label>
              <input data-testid="register-company" type="text" value={form.company_name}
                onChange={e => update("company_name", e.target.value)} placeholder={isPartner ? "BrightEdge Consulting" : "Acme Corp"} required
                className="w-full h-11 px-4 rounded-lg text-sm outline-none transition-all"
                style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #CDD2DE", color: "#1C2040" }}
                onFocus={e => e.target.style.borderColor = "#1B75BA"} onBlur={e => e.target.style.borderColor = "#CDD2DE"} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "#1C2040" }}>Industry</label>
              <div className="grid grid-cols-2 gap-2">
                {INDUSTRIES.map(ind => (
                  <button key={ind} data-testid={`industry-${ind.toLowerCase().replace(/[^a-z]/g, "-")}`} type="button"
                    onClick={() => update("industry", ind)}
                    className="text-left px-3 py-2 rounded-lg text-xs font-medium transition-all"
                    style={{
                      border: form.industry === ind ? "2px solid #1B75BA" : "1.5px solid #CDD2DE",
                      backgroundColor: form.industry === ind ? "#EFF6FF" : "white",
                      color: form.industry === ind ? "#1B75BA" : "#1C2040",
                    }}>
                    {ind}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "#1C2040" }}>
                {isPartner ? "Team Size" : "Number of Employees"}
              </label>
              <div className="flex flex-wrap gap-2">
                {EMPLOYEE_COUNTS.map(ec => (
                  <button key={ec} data-testid={`emp-count-${ec.replace(/[^a-z0-9]/gi, "-")}`} type="button"
                    onClick={() => update("employee_count", ec)}
                    className="px-4 py-2 rounded-lg text-xs font-semibold transition-all"
                    style={{
                      border: form.employee_count === ec ? "2px solid #1B75BA" : "1.5px solid #CDD2DE",
                      backgroundColor: form.employee_count === ec ? "#EFF6FF" : "white",
                      color: form.employee_count === ec ? "#1B75BA" : "#1C2040",
                    }}>
                    {ec}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#1C2040" }}>Phone (optional)</label>
                <input data-testid="register-phone" type="tel" value={form.phone} onChange={e => update("phone", e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="w-full h-11 px-4 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #CDD2DE", color: "#1C2040" }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#1C2040" }}>Website (optional)</label>
                <input data-testid="register-website" type="url" value={form.website} onChange={e => update("website", e.target.value)}
                  placeholder="https://company.com"
                  className="w-full h-11 px-4 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #CDD2DE", color: "#1C2040" }} />
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button type="button" onClick={prevStep} className="h-11 px-5 rounded-lg font-medium text-sm flex items-center gap-1.5" style={{ border: "1.5px solid #CDD2DE", color: "#6B7491" }}>
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <button data-testid="step-next" type="button" onClick={nextStep} disabled={!canProceedStep4}
              className="flex-1 h-11 rounded-lg font-semibold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: "#1B75BA" }}>
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </>
      );
    }

    // Email Verification step (step 4 for partner, step 5 for business)
    const verifyStep = isPartner ? 4 : 5;
    if (step === verifyStep) {
      return (
        <>
          <div className="mb-6 text-center">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "#EFF6FF" }}>
              <Mail className="w-8 h-8" style={{ color: "#1B75BA" }} />
            </div>
            <h2 className="text-2xl font-bold mb-1" style={{ color: "#1C2040" }}>Verify your email</h2>
            <p className="text-sm" style={{ color: "#6B7491" }}>We'll send a 6-digit code to <strong style={{ color: "#1C2040" }}>{form.email}</strong></p>
          </div>

          {!codeSent ? (
            <button data-testid="send-code-btn" type="button" onClick={handleSendCode} disabled={sendingCode}
              className="w-full h-11 rounded-lg font-semibold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: "#1B75BA" }}>
              {sendingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" /> Send Verification Code</>}
            </button>
          ) : !codeVerified ? (
            <div className="space-y-4">
              <OTPInput value={verifyCode} onChange={setVerifyCode} />
              {demoCode && (
                <div className="text-center p-3 rounded-xl" style={{ backgroundColor: "#FFFBEB", border: "1px dashed #D97706" }}>
                  <p className="text-[10px] font-bold" style={{ color: "#92400E" }}>DEMO MODE - Your code is: <span className="text-sm font-mono">{demoCode}</span></p>
                </div>
              )}
              <button data-testid="verify-code-btn" type="button" onClick={handleVerifyCode}
                disabled={verifyCode.length !== 6 || verifying}
                className="w-full h-11 rounded-lg font-semibold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: "#1B75BA" }}>
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify Code"}
              </button>
              <button type="button" onClick={handleSendCode}
                className="w-full text-center text-xs font-medium" style={{ color: "#1B75BA" }}>
                Resend code
              </button>
            </div>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: "#F0FDF4" }}>
                <Check className="w-7 h-7" style={{ color: "#D4A017" }} />
              </div>
              <p className="text-sm font-semibold" style={{ color: "#D4A017" }}>Email verified successfully!</p>
              <button data-testid="continue-after-verify" type="button" onClick={nextStep} disabled={loading}
                className="w-full h-11 rounded-lg font-semibold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: "#1B75BA" }}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /> Create Account & Continue</>}
              </button>
            </div>
          )}

          <div className="flex gap-3 mt-4">
            <button type="button" onClick={prevStep} className="w-full h-9 rounded-lg font-medium text-xs flex items-center justify-center gap-1.5" style={{ border: "1.5px solid #CDD2DE", color: "#6B7491" }}>
              <ChevronLeft className="w-3.5 h-3.5" /> Back
            </button>
          </div>
        </>
      );
    }

    // Security / 2FA step (last step - step 5 for partner, step 6 for business)
    const securityStep = isPartner ? 5 : 6;
    if (step === securityStep) {
      return (
        <>
          <div className="mb-6 text-center">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "#F0FDF4" }}>
              <Shield className="w-8 h-8" style={{ color: "#D4A017" }} />
            </div>
            <h2 className="text-2xl font-bold mb-1" style={{ color: "#1C2040" }}>Secure your account</h2>
            <p className="text-sm" style={{ color: "#6B7491" }}>Add two-factor authentication for extra security</p>
          </div>

          {/* Free trial banner */}
          <div className="p-4 rounded-xl mb-6 text-center" style={{ background: "linear-gradient(135deg, #EFF6FF, #F0FDF4)", border: "1.5px solid #FED7AA" }}>
            <div className="flex items-center justify-center gap-2 mb-1">
              <Sparkles className="w-4 h-4" style={{ color: "#D4A017" }} />
              <span className="text-sm font-bold" style={{ color: "#D4A017" }}>14-Day Free Trial Activated</span>
            </div>
            <p className="text-xs" style={{ color: "#6B7491" }}>No credit card required. Full access to all features.</p>
          </div>

          {!setup2fa ? (
            <div className="space-y-3">
              <button data-testid="setup-2fa-btn" type="button" onClick={handleSetup2FA}
                className="w-full p-4 rounded-xl text-left flex items-center gap-4 transition-all hover:shadow-sm"
                style={{ border: "1.5px solid #CDD2DE" }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#F0FDF4" }}>
                  <QrCode className="w-5 h-5" style={{ color: "#D4A017" }} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: "#1C2040" }}>Set up authenticator app</p>
                  <p className="text-xs" style={{ color: "#6B7491" }}>Use Google Authenticator, Authy, or 1Password</p>
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: "#CDD2DE" }} />
              </button>

              <button data-testid="skip-2fa-btn" type="button" onClick={finishRegistration}
                className="w-full h-11 rounded-lg font-semibold text-sm flex items-center justify-center gap-2"
                style={{ border: "1.5px solid #CDD2DE", color: "#6B7491" }}>
                Skip for now <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 rounded-xl text-center" style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #CDD2DE" }}>
                <p className="text-[10px] font-medium mb-2 uppercase tracking-wider" style={{ color: "#6B7491" }}>Scan this QR code with your authenticator app</p>
                <div className="w-40 h-40 mx-auto rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: "white", border: "2px solid #E5E7EB" }}>
                  <div className="text-center">
                    <QrCode className="w-16 h-16 mx-auto mb-1" style={{ color: "#1C2040" }} />
                    <p className="text-[8px]" style={{ color: "#9BA4B8" }}>Demo QR Code</p>
                  </div>
                </div>
                <p className="text-[10px] mb-1" style={{ color: "#6B7491" }}>Or enter this key manually:</p>
                <p className="text-xs font-mono font-bold tracking-wider px-3 py-1.5 rounded-lg inline-block" style={{ backgroundColor: "#EFF6FF", color: "#1B75BA" }}>
                  {totpSecret ? `${totpSecret.slice(0, 4)} ${totpSecret.slice(4, 8)} ${totpSecret.slice(8, 12)} ${totpSecret.slice(12, 16)}` : ""}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5 text-center" style={{ color: "#1C2040" }}>Enter the 6-digit code from your app</label>
                <OTPInput value={totpCode} onChange={setTotpCode} />
              </div>
              <button data-testid="verify-2fa-btn" type="button" onClick={handleVerify2FA}
                disabled={totpCode.length !== 6}
                className="w-full h-11 rounded-lg font-semibold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: "#D4A017" }}>
                <Shield className="w-4 h-4" /> Enable 2FA & Start Free Trial
              </button>
              <button type="button" onClick={finishRegistration}
                className="w-full text-center text-xs font-medium" style={{ color: "#6B7491" }}>
                Skip 2FA setup
              </button>
            </div>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen flex overflow-hidden" data-testid="register-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-5/12 relative flex-col justify-between p-12" style={{ backgroundColor: "#282560" }}>
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 30% 50%, #1B75BA 0%, transparent 60%)" }} />
        <div className="relative z-10" />
        <div className="relative z-10 flex flex-col items-center text-center">
          <img src="/images/logo-white.png" alt="OnPoint BI" className="w-72 h-auto mb-10" data-testid="register-hero-logo" />
          <p className="text-white/60 text-sm font-medium mb-3 uppercase tracking-widest">Step {step} of {totalSteps}</p>
          <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
            {step === 1 ? "Let's Create your OnPoint Account" :
              logicalStep <= 3 ? "Tell us about yourself" :
              logicalStep === 4 ? "Almost there" :
              logicalStep === 5 ? "Verify your identity" :
              "You're all set!"}
          </h1>
          <p className="text-white/50 text-sm leading-relaxed mb-8 max-w-xs">
            {isPartner ? "Manage multiple clients with white-label branding and aggregated reporting."
              : "Unify your financial, CRM, and operational data in one intelligent dashboard."}
          </p>
          <div className="space-y-2 text-left w-full max-w-xs">
            {(isPartner
              ? ["White-label partner portal", "Client account management", "Aggregated multi-client reporting", "Dedicated partner support"]
              : ["Real-time financial dashboard", "AI-powered forecasting", "Multi-source data aggregation", "Enterprise-grade security"]
            ).map(f => (
              <div key={f} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(0,127,217,0.3)" }}>
                  <Check className="w-3 h-3" style={{ color: "#B8D8F2" }} />
                </div>
                <span className="text-white/60 text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <p className="text-white/25 text-xs">&copy; 2026 OnPoint Business Intelligence, LLC</p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-7/12 flex items-center justify-center p-6 lg:p-14 bg-white overflow-y-auto">
        <div className="w-full max-w-lg animate-fade-in">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-6">
            <img src="/images/logo-colored.png" alt="OnPoint BI" className="w-56 h-auto" data-testid="register-mobile-logo" />
          </div>

          <StepIndicator current={step} total={totalSteps} labels={stepLabels} />

          {renderStep()}

          <p className="mt-6 text-center text-sm" style={{ color: "#6B7491" }}>
            Already have an account?{" "}
            <Link to="/login" data-testid="go-to-login-link" className="font-semibold hover:underline" style={{ color: "#1B75BA" }}>Sign in</Link>
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 lg:left-auto lg:w-7/12">
          <Footer />
        </div>
      </div>
    </div>
  );
}
